import type {
  Product,
  Order,
  Customer,
  Coupon,
  Store,
  StoreStats,
  PaymentSettings,
  CartItem,
  Address,
  CheckoutCalculation,
  JjsPaymentInitRequest,
  JjsPaymentInitResponse,
  ShipStationCatalogIngestSummary,
  ShipStationFamilyProposal,
  ShipStationMapping,
  ShipStationMappingsListResponse,
  ShipStationMappingsQuery,
  ShipStationInventorySyncSummary,
  ShipStationFulfillmentRatesRequest,
  ShipStationFulfillmentRatesResponse,
  ShipStationValidateAddressInput,
  ShipStationAddressValidationResult,
  ShipStationCreateFulfillmentBatchRequest,
  ShipStationFulfillmentBatchResponse,
  ShipStationCreateManifestRequest,
  ShipStationManifestResponse,
  ShipStationFulfillmentCompleteRequest,
  ShipStationFulfillmentCompleteResult,
  MetafieldDefinition,
  EntityMetafield,
} from '../types/index.js';
import {
  DEFAULT_INTEGRATION_PROVIDERS,
  mergeIntegrationProviders,
  type IntegrationsListResponse,
  type ShipStationIntegrationResponse,
  type ShipStationIntegrationUpdateRequest,
  type ShipStationTestResponse,
  type CartRoverIntegrationResponse,
  type CartRoverIntegrationUpdateRequest,
  type CartRoverTestResponse,
  type CartRoverInventorySyncSummary,
} from '../integrations/types.js';
import {
  DEFAULT_SHIPSTATION_CONFIG,
  mergeShipStationConfig,
  mergeShipStationCredentialsUpdate,
  redactCredentials,
  validateShipStationConfig,
  validateShipStationCredentials,
  validateShipStationStoreId,
  type ShipStationCredentials,
} from '../integrations/shipstation/config.js';
import {
  matchesIntegrationExportStatusFilter,
  mergeShipStationOrderMetadata,
  parseIntegrationExportStatusFilter,
  type ShipStationExportSummary,
} from '../integrations/shipstation/order-export.js';
import { DEFAULT_JJS_PROCESSOR_SETTINGS } from '../types/processor-config.js';
import {
  mockProducts,
  mockOrders,
  mockCustomers,
  mockCoupons,
  mockStore,
  mockStoreStats,
  getProductById,
  searchProducts,
  getProductsByCategory,
  getFeaturedProducts,
  getRelatedProducts,
} from '../mock/index.js';
import { getStoreApiConfig, isApiModeEnabled, getDefaultStoreId } from './config.js';
import { StoreApiClient, type CheckoutProcessResult, type CouponValidationResult } from './client.js';
import {
  resolveSelectedShippingPrice,
  resolveShippingOptions,
} from '../utils/shipping.js';

let cachedClient: StoreApiClient | null = null;
let cachedConfigKey = '';

let mockShipStationCredentials: ShipStationCredentials | null = null;

function buildMockShipStationIntegration(): ShipStationIntegrationResponse {
  const config = { ...DEFAULT_SHIPSTATION_CONFIG };
  const credentialsValid = validateShipStationCredentials(mockShipStationCredentials).valid;
  const storeIdValid = validateShipStationStoreId(config.shipStationStoreId).valid;

  return {
    id: 'int-shipstation-mock',
    storeId: getDefaultStoreId(),
    provider: 'shipstation',
    enabled: false,
    testMode: true,
    config,
    credentials: redactCredentials(mockShipStationCredentials),
    providers: { ...DEFAULT_INTEGRATION_PROVIDERS },
  };
}

let mockShipStationIntegration: ShipStationIntegrationResponse = buildMockShipStationIntegration();

function getClient(): StoreApiClient | null {
  const config = getStoreApiConfig();
  if (!config) return null;
  const key = `${config.baseUrl}|${config.storeId}|${config.authToken ?? ''}`;
  if (!cachedClient || cachedConfigKey !== key) {
    cachedClient = new StoreApiClient(config);
    cachedConfigKey = key;
  }
  return cachedClient;
}

/** Clears the module-level API client cache (embedded admin runtime re-bootstrap). */
export function resetStoreApiClientCache(): void {
  cachedClient = null;
  cachedConfigKey = '';
}

/** Unified data access: API when configured, otherwise in-package mock */
export const storeApi = {
  isApiMode(): boolean {
    return isApiModeEnabled();
  },

  getStoreId(): string {
    return getDefaultStoreId();
  },

  async getProducts(options?: { q?: string; category?: string }): Promise<Product[]> {
    const client = getClient();
    if (client) {
      return client.getProducts(options);
    }
    if (options?.q || options?.category) {
      return searchProducts(options.q ?? '', options.category);
    }
    return [...mockProducts];
  },

  async getProduct(productId: string): Promise<Product | undefined> {
    const client = getClient();
    if (client) {
      try {
        return await client.getProduct(productId);
      } catch {
        return undefined;
      }
    }
    return getProductById(productId);
  },

  async searchProducts(query: string, category?: string): Promise<Product[]> {
    return this.getProducts({ q: query, category });
  },

  getProductsByCategory(category: string): Promise<Product[]> {
    return this.getProducts({ category });
  },

  async getFeaturedProducts(limit = 4): Promise<Product[]> {
    const client = getClient();
    if (client) {
      const all = await client.getProducts();
      return all
        .filter((p) => p.tags?.includes('bestseller') || p.inventoryQuantity > 100)
        .slice(0, limit);
    }
    return getFeaturedProducts(limit);
  },

  async getRelatedProducts(productId: string, limit = 4): Promise<Product[]> {
    const client = getClient();
    if (client) {
      const product = await client.getProduct(productId);
      if (!product?.category) return [];
      const all = await client.getProducts({ category: product.category });
      return all.filter((p) => p.id !== productId).slice(0, limit);
    }
    return getRelatedProducts(productId, limit);
  },

  async getOrders(params?: { shipstationExportStatus?: string }): Promise<Order[]> {
    const client = getClient();
    if (client) return client.getOrders(params);
    const orders = [...mockOrders];
    if (!params?.shipstationExportStatus) {
      return orders;
    }
    const filter = parseIntegrationExportStatusFilter(params.shipstationExportStatus);
    if (!filter) {
      return orders;
    }
    return orders.filter((order) =>
      matchesIntegrationExportStatusFilter(order.metadata, filter)
    );
  },

  async getIntegrations(): Promise<IntegrationsListResponse> {
    const client = getClient();
    if (client) return client.getIntegrations();
    const integration = mockShipStationIntegration;
    const credentialsValid = validateShipStationCredentials(mockShipStationCredentials).valid;
    const storeIdValid = validateShipStationStoreId(integration.config.shipStationStoreId).valid;
    return {
      providers: integration.providers,
      modules: [
        {
          id: 'shipstation',
          label: 'ShipStation',
          capabilities: ['orderExport', 'inventory', 'fulfillment', 'catalogIngest'],
          enabled: integration.enabled,
          configured: credentialsValid && storeIdValid,
        },
      ],
    };
  },

  async getExtensions(): Promise<any> {
    const client = getClient();
    if (client) return client.getExtensions();
    return { data: [] };
  },

  async installExtension(manifestJson: any): Promise<any> {
    const client = getClient();
    if (client) return client.installExtension(manifestJson);
    return { success: true };
  },

  async getShipStationIntegration(): Promise<ShipStationIntegrationResponse> {
    const client = getClient();
    if (client) return client.getShipStationIntegration();
    return {
      ...mockShipStationIntegration,
      credentials: redactCredentials(mockShipStationCredentials),
    };
  },

  async updateShipStationIntegration(
    body: ShipStationIntegrationUpdateRequest
  ): Promise<ShipStationIntegrationResponse> {
    const client = getClient();
    if (client) return client.updateShipStationIntegration(body);

    let nextConfig = mockShipStationIntegration.config;
    if (body.config !== undefined) {
      nextConfig = mergeShipStationConfig(nextConfig, body.config);
    }

    if (body.credentials !== undefined) {
      if (body.credentials === null) {
        mockShipStationCredentials = null;
      } else {
        mockShipStationCredentials = mergeShipStationCredentialsUpdate(
          mockShipStationCredentials,
          body.credentials
        );
        const validation = validateShipStationCredentials(mockShipStationCredentials);
        if (!validation.valid) {
          throw new Error(validation.message ?? 'Invalid ShipStation credentials');
        }
      }
    }

    const enabled =
      body.enabled === undefined ? mockShipStationIntegration.enabled : Boolean(body.enabled);
    const testMode =
      body.testMode === undefined ? mockShipStationIntegration.testMode : Boolean(body.testMode);

    if (enabled) {
      const configValidation = validateShipStationConfig(nextConfig, { requireStoreId: true });
      if (!configValidation.valid) {
        throw new Error(
          configValidation.message ?? 'ShipStation store ID is required when integration is enabled'
        );
      }
    }

    const providers =
      body.providers !== undefined
        ? mergeIntegrationProviders({
            ...mockShipStationIntegration.providers,
            ...body.providers,
          })
        : mockShipStationIntegration.providers;

    mockShipStationIntegration = {
      ...mockShipStationIntegration,
      enabled,
      testMode,
      config: nextConfig,
      providers,
      credentials: redactCredentials(mockShipStationCredentials),
    };

    return this.getShipStationIntegration();
  },

  async testShipStationConnection(): Promise<ShipStationTestResponse> {
    const client = getClient();
    if (client) return client.testShipStationConnection();

    const validation = validateShipStationCredentials(mockShipStationCredentials);
    if (!validation.valid) {
      throw new Error(validation.message ?? 'ShipStation credentials not configured');
    }

    return {
      ok: true,
      message: 'ShipStation V1 connection successful (mock mode)',
      productCountSample: 1,
      totalProducts: 10,
    };
  },

  async getCartRoverIntegration(): Promise<CartRoverIntegrationResponse> {
    const client = getClient();
    if (client) return client.getCartRoverIntegration();
    throw new Error('CartRover integration requires API mode');
  },

  async updateCartRoverIntegration(
    body: CartRoverIntegrationUpdateRequest
  ): Promise<CartRoverIntegrationResponse> {
    const client = getClient();
    if (client) return client.updateCartRoverIntegration(body);
    throw new Error('CartRover integration requires API mode');
  },

  async testCartRoverConnection(): Promise<CartRoverTestResponse> {
    const client = getClient();
    if (client) return client.testCartRoverConnection();
    return { ok: true, message: 'CartRover connection successful (mock mode)', inventorySampleCount: 1 };
  },

  async syncCartRoverInventory(
    options?: { offset?: number }
  ): Promise<CartRoverInventorySyncSummary> {
    const client = getClient();
    if (client) return client.syncCartRoverInventory(options);
    return {
      totalSynced: 0,
      updatedCount: 0,
      offset: 0,
      totalAvailable: 0,
      hasMore: false,
    };
  },

  async syncCartRoverInventoryAll(): Promise<CartRoverInventorySyncSummary> {
    const client = getClient();
    if (client) return client.syncCartRoverInventoryAll();
    return {
      totalSynced: 0,
      updatedCount: 0,
      offset: 0,
      totalAvailable: 0,
      hasMore: false,
    };
  },

  async getWebhooks(): Promise<any> {
    const client = getClient();
    if (client) return client.getWebhooks();
    return { webhooks: [] };
  },

  async createWebhook(url: string, topics: string[]): Promise<any> {
    const client = getClient();
    if (client) return client.createWebhook(url, topics);
    return { success: true };
  },

  async getDeliveryLog(): Promise<any> {
    const client = getClient();
    if (client) return client.getDeliveryLog();
    return { deliveries: [] };
  },

  async retryDelivery(deliveryId: string): Promise<any> {
    const client = getClient();
    if (client) return client.retryDelivery(deliveryId);
    return { success: true };
  },

  async getThemeProfiles(): Promise<any> {
    const client = getClient();
    if (client) return client.getThemeProfiles();
    return { profiles: [] };
  },

  async createThemeProfile(name: string, tokens: Record<string, string>): Promise<any> {
    const client = getClient();
    if (client) return client.createThemeProfile(name, tokens);
    return { success: true };
  },

  async exportShipStationOrders(body?: {
    orderIds?: string[];
  }): Promise<ShipStationExportSummary> {
    const client = getClient();
    if (client) return client.exportShipStationOrders(body);
    return {
      exported: 0,
      failed: 0,
      blocked: 0,
      skipped: 0,
      results: [],
    };
  },

  async ingestShipStationCatalog(): Promise<ShipStationCatalogIngestSummary> {
    const client = getClient();
    if (client) return client.ingestShipStationCatalogAll();
    return {
      totalIngested: 0,
      newSkus: 0,
      updatedSkus: 0,
      page: 1,
      totalPages: 1,
      hasMore: false,
    };
  },

  async proposeShipStationFamilies(): Promise<ShipStationFamilyProposal[]> {
    const client = getClient();
    if (client) return client.proposeShipStationFamilies();
    return [];
  },

  async getShipStationMappings(
    params?: ShipStationMappingsQuery
  ): Promise<ShipStationMappingsListResponse> {
    const client = getClient();
    if (client) return client.getShipStationMappings(params);
    return {
      mappings: [],
      total: 0,
      unmappedTotal: 0,
      mappedTotal: 0,
      limit: 0,
      offset: 0,
    };
  },

  async updateShipStationMappings(body: {
    mappings: ShipStationMapping[];
  }): Promise<{ updated: number }> {
    const client = getClient();
    if (client) return client.updateShipStationMappings(body);
    return { updated: 0 };
  },

  async syncShipStationInventory(): Promise<ShipStationInventorySyncSummary> {
    const client = getClient();
    if (client) return client.syncShipStationInventoryAll();
    return {
      totalSynced: 0,
      updatedCount: 0,
      offset: 0,
      totalAvailable: 0,
      hasMore: false,
    };
  },

  async validateShipStationAddresses(body: {
    addresses: ShipStationValidateAddressInput[];
  }): Promise<{ results: ShipStationAddressValidationResult[] }> {
    const client = getClient();
    if (client) return client.validateShipStationAddresses(body);
    return { results: body.addresses.map((addr) => ({ orderId: addr.orderId, status: 'verified' as const, messages: [] })) };
  },

  async getShipStationFulfillmentRates(
    body: ShipStationFulfillmentRatesRequest
  ): Promise<ShipStationFulfillmentRatesResponse> {
    const client = getClient();
    if (client) return client.getShipStationFulfillmentRates(body);
    return { rates: [] };
  },

  async createShipStationFulfillmentBatch(
    body: ShipStationCreateFulfillmentBatchRequest
  ): Promise<ShipStationFulfillmentBatchResponse> {
    const client = getClient();
    if (client) return client.createShipStationFulfillmentBatch(body);
    return { batchId: `bat_FAKE_${Date.now()}`, status: 'created' };
  },

  async getShipStationFulfillmentBatchLabels(batchId: string) {
    const client = getClient();
    if (client) return client.getShipStationFulfillmentBatchLabels(batchId);
    return { labels: [] };
  },

  async createShipStationFulfillmentManifest(
    body: ShipStationCreateManifestRequest
  ): Promise<ShipStationManifestResponse> {
    const client = getClient();
    if (client) return client.createShipStationFulfillmentManifest(body);
    return { manifestId: `man_FAKE_${Date.now()}` };
  },

  async completeShipStationFulfillment(
    body: ShipStationFulfillmentCompleteRequest
  ): Promise<ShipStationFulfillmentCompleteResult> {
    const client = getClient();
    if (client) return client.completeShipStationFulfillment(body);
    const updatedIds: string[] = [];
    for (const item of body.orders) {
      const idx = mockOrders.findIndex((o) => o.id === item.orderId);
      if (idx < 0) {
        continue;
      }
      const metadata = mergeShipStationOrderMetadata(mockOrders[idx].metadata, {
        batchId: body.batchId,
        shipmentId: item.shipmentId,
        trackingNumber: item.trackingNumber,
        labelUrl: item.labelUrl,
      });
      mockOrders[idx] = {
        ...mockOrders[idx],
        status: 'shipped',
        trackingNumber: item.trackingNumber,
        shippingMethod: item.shippingMethod,
        metadata,
        updatedAt: new Date().toISOString(),
      };
      updatedIds.push(item.orderId);
    }
    return { updated: updatedIds.length, orderIds: updatedIds };
  },

  async updateOrder(orderId: string, partial: Partial<Order>): Promise<Order> {
    const client = getClient();
    if (client) return client.updateOrder(orderId, partial);
    const idx = mockOrders.findIndex((o) => o.id === orderId);
    if (idx < 0) throw new Error('Order not found');
    mockOrders[idx] = { ...mockOrders[idx], ...partial, updatedAt: new Date().toISOString() };
    return mockOrders[idx];
  },

  async getCustomers(): Promise<Customer[]> {
    const client = getClient();
    if (client) return client.getCustomers();
    return [...mockCustomers];
  },

  async getCoupons(): Promise<Coupon[]> {
    const client = getClient();
    if (client) return client.getCoupons();
    return [...mockCoupons];
  },

  async getStore(): Promise<Store> {
    const client = getClient();
    if (client) return client.getStore();
    return { ...mockStore };
  },

  async updateStore(partial: Partial<Store>): Promise<Store> {
    const client = getClient();
    if (client) return client.updateStore(partial);
    Object.assign(mockStore, partial);
    return { ...mockStore };
  },

  async getStoreStats(): Promise<StoreStats> {
    const client = getClient();
    if (client) return client.getStoreStats();
    return { ...mockStoreStats };
  },

  async getPaymentSettings(): Promise<PaymentSettings> {
    const client = getClient();
    if (client) return client.getPaymentSettings();
    return {
      id: 'pay-local',
      storeId: getDefaultStoreId(),
      gateway: 'nmi',
      testMode: true,
      enabledMethods: ['jjs'],
      processorConfig: { jjs: DEFAULT_JJS_PROCESSOR_SETTINGS },
    };
  },

  async updatePaymentSettings(
    partial: Partial<PaymentSettings>
  ): Promise<PaymentSettings> {
    const client = getClient();
    if (client) return client.updatePaymentSettings(partial);
    return {
      id: partial.id ?? 'pay-local',
      storeId: partial.storeId ?? getDefaultStoreId(),
      gateway: partial.gateway ?? 'nmi',
      testMode: partial.testMode ?? true,
      enabledMethods: partial.enabledMethods ?? ['jjs'],
      processorConfig: partial.processorConfig ?? { jjs: DEFAULT_JJS_PROCESSOR_SETTINGS },
    };
  },

  /** Phase 2: delegates to Worker init handler (wallet.php + redirect URL) */
  async initJjsPayment(body: JjsPaymentInitRequest): Promise<JjsPaymentInitResponse> {
    const client = getClient();
    if (client) return client.initJjsPayment(body);
    throw new Error('JJS payment init requires API mode (LANDI_STORE_API_BASE)');
  },

  async createProduct(product: Product): Promise<Product> {
    const client = getClient();
    if (client) return client.createProduct(product);
    mockProducts.push(product);
    return product;
  },

  async updateProduct(productId: string, partial: Partial<Product>): Promise<Product> {
    const client = getClient();
    if (client) return client.updateProduct(productId, partial);
    const idx = mockProducts.findIndex((p) => p.id === productId);
    if (idx < 0) throw new Error('Product not found');
    mockProducts[idx] = { ...mockProducts[idx], ...partial };
    return mockProducts[idx];
  },

  async deleteProduct(productId: string): Promise<void> {
    const client = getClient();
    if (client) {
      await client.deleteProduct(productId);
      return;
    }
    const idx = mockProducts.findIndex((p) => p.id === productId);
    if (idx >= 0) mockProducts.splice(idx, 1);
  },

  async uploadProductImages(files: File[]) {
    const client = getClient();
    if (!client) {
      throw new Error('Image upload requires API mode (LANDI_STORE_API_BASE)');
    }
    return client.uploadProductImages(files);
  },

  async listMedia(params?: { limit?: number; cursor?: string; status?: import('./client.js').StoreMediaStatus }) {
    const client = getClient();
    if (!client) {
      throw new Error('Media library requires API mode (LANDI_STORE_API_BASE)');
    }
    return client.listMedia(params);
  },

  async presignMediaUpload(body: import('./client.js').StoreMediaPresignRequest) {
    const client = getClient();
    if (!client) {
      throw new Error('Media presign requires API mode (LANDI_STORE_API_BASE)');
    }
    return client.presignMediaUpload(body);
  },

  async registerMedia(body: import('./client.js').StoreMediaRegisterRequest) {
    const client = getClient();
    if (!client) {
      throw new Error('Media register requires API mode (LANDI_STORE_API_BASE)');
    }
    return client.registerMedia(body);
  },

  async getMedia(mediaId: string) {
    const client = getClient();
    if (!client) {
      throw new Error('Media detail requires API mode (LANDI_STORE_API_BASE)');
    }
    return client.getMedia(mediaId);
  },

  async putMediaContent(
    mediaId: string,
    body: Uint8Array | ArrayBuffer,
    mimeType: string
  ): Promise<void> {
    const client = getClient();
    if (!client) {
      throw new Error('Media upload requires API mode (LANDI_STORE_API_BASE)');
    }
    return client.putMediaContent(mediaId, body, mimeType);
  },

  async deleteMedia(mediaId: string) {
    const client = getClient();
    if (!client) {
      throw new Error('Media delete requires API mode (LANDI_STORE_API_BASE)');
    }
    return client.deleteMedia(mediaId);
  },

  async createCoupon(coupon: Coupon): Promise<Coupon> {
    const client = getClient();
    if (client) return client.createCoupon(coupon);
    mockCoupons.push(coupon);
    return coupon;
  },

  async updateCoupon(couponId: string, partial: Partial<Coupon>): Promise<Coupon> {
    const client = getClient();
    if (client) return client.updateCoupon(couponId, partial);
    const idx = mockCoupons.findIndex((c) => c.id === couponId);
    if (idx < 0) throw new Error('Coupon not found');
    mockCoupons[idx] = { ...mockCoupons[idx], ...partial };
    return mockCoupons[idx];
  },

  async deleteCoupon(couponId: string): Promise<void> {
    const client = getClient();
    if (client) {
      await client.deleteCoupon(couponId);
      return;
    }
    const idx = mockCoupons.findIndex((c) => c.id === couponId);
    if (idx >= 0) mockCoupons.splice(idx, 1);
  },

  async checkoutCalculate(body: {
    items: CartItem[];
    couponCode?: string;
    shippingAddress?: Partial<Address>;
    shippingMethodId?: string;
  }): Promise<CheckoutCalculation> {
    const client = getClient();
    if (client) return client.checkoutCalculate(body);
    const subtotal = body.items.reduce((s, i) => s + i.price * i.quantity, 0);
    const tax = subtotal * (mockStore.settings.taxRate / 100);
    const resolved = resolveShippingOptions({
      zones: mockStore.settings.shippingZones,
      countryCode: body.shippingAddress?.country ?? 'US',
      subtotal,
    });
    const shipping = resolveSelectedShippingPrice(
      resolved.options,
      body.shippingMethodId
    );
    return {
      subtotal,
      tax,
      shipping,
      discount: 0,
      total: subtotal + tax + shipping,
      shippingOptions: resolved.options,
    };
  },

  async checkoutProcess(body: {
    items: CartItem[];
    couponCode?: string;
    shippingAddress?: Partial<Address>;
    shippingMethod?: string;
    email?: string;
  }): Promise<CheckoutProcessResult> {
    const client = getClient();
    if (client) return client.checkoutProcess(body);
    const calc = await this.checkoutCalculate(body);
    return {
      orderId: `ORD-LOCAL-${Date.now()}`,
      total: calc.total,
    };
  },

  async validateCoupon(code: string, orderAmount?: number): Promise<CouponValidationResult> {
    const client = getClient();
    if (client) return client.validateCoupon(code, orderAmount);
    const coupon = mockCoupons.find(
      (c) => c.code.toUpperCase() === code.toUpperCase() && c.active
    );
    if (!coupon) {
      return { valid: false, discount: 0, message: 'Invalid coupon' };
    }
    const amount = orderAmount ?? 0;
    if (coupon.minOrderValue && amount < coupon.minOrderValue) {
      return { valid: false, discount: 0, message: 'Minimum not met' };
    }
    let discount = 0;
    if (coupon.discountType === 'percentage') {
      discount = amount * (coupon.discountValue / 100);
    } else if (coupon.discountType === 'fixed') {
      discount = coupon.discountValue;
    }
    return { valid: true, discount, coupon };
  },

  // Metafields
  async getMetafieldDefinitions(storeId: string): Promise<MetafieldDefinition[]> {
    const client = getClient();
    if (client) return client.getMetafieldDefinitions(storeId);
    return [];
  },

  async createMetafieldDefinition(storeId: string, definition: Partial<MetafieldDefinition>): Promise<{ id: string }> {
    const client = getClient();
    if (client) return client.createMetafieldDefinition(storeId, definition);
    return { id: `def_${Date.now()}` };
  },

  async getEntityMetafields(storeId: string, ownerType: string, ownerId: string): Promise<EntityMetafield[]> {
    const client = getClient();
    if (client) return client.getEntityMetafields(storeId, ownerType, ownerId);
    return [];
  },

  async upsertEntityMetafield(storeId: string, ownerType: string, ownerId: string, namespace: string, key: string, value: any): Promise<void> {
    const client = getClient();
    if (client) return client.upsertEntityMetafield(storeId, ownerType, ownerId, namespace, key, value);
  },

  async deleteEntityMetafield(storeId: string, ownerType: string, ownerId: string, namespace: string, key: string): Promise<void> {
    const client = getClient();
    if (client) return client.deleteEntityMetafield(storeId, ownerType, ownerId, namespace, key);
  },
};

export { StoreApiClient, StoreApiError } from './client.js';
export type {
  CheckoutProcessResult,
  CouponValidationResult,
  ProductImageUploadResult,
  StoreMediaRecord,
  StoreMediaListResult,
  StoreMediaPresignRequest,
  StoreMediaPresignResult,
  StoreMediaRegisterRequest,
  StoreMediaStatus,
} from './client.js';
